import { RiskBandsAdvancedSettingsRefs } from "@/components/layouts/RiskBandsAdvancedSettings";
import { getBaseThemeName, getKbConfig, getRiskBandsMode } from "@/features/branding";
import { GeocodingHelpButton } from "@/features/geocoders";
import { InteractiveTutorial, InteractiveTutorialProps } from "@/features/interactive_tutorial";
import { ScreeningReviewModalRefs } from "@/tools/risk-bands/components/ScreeningLimitReview";
import { SingleResultModalRefs } from "@/tools/risk-bands/components/SingleResultModal";
import { Alert, Image, TourStepProps } from "antd";
import { LatLngLiteral } from "leaflet";
import Link from "next/link";
import { MutableRefObject, useEffect } from "react";
import { useTranslation } from "react-i18next";
export interface SingleAssetScreenState {
  address: string;
  position: LatLngLiteral;
}
const tutorialState: SingleAssetScreenState = {
  address: "Disneyland Hong Kong, Lantau Island, Hong Kong",
  position: {
    lat: 22.3129666,
    lng: 114.0412819
  }
};
export interface SingleAssetScreenTutorial extends InteractiveTutorialProps<SingleAssetScreenState> {
  refs: {
    address: MutableRefObject<null>;
    archetype: MutableRefObject<null>;
    buildYear: MutableRefObject<null>;
    heightAboveGound: MutableRefObject<null>;
    advancedCollapse: MutableRefObject<null>;
    reviewModalTrigger: MutableRefObject<null>;
    reviewModal?: ScreeningReviewModalRefs;
    map: MutableRefObject<null>;
    advancedSettings?: RiskBandsAdvancedSettingsRefs;
    modal?: SingleResultModalRefs;
  };
}
export const SingleAssetScreenTutorial = ({
  refs,
  ...props
}: SingleAssetScreenTutorial) => {
  const {
    t
  } = useTranslation("terms");
  const {
    t: settings
  } = useTranslation("tools", {
    keyPrefix: "settings"
  });
  useEffect(() => {
    if (props.open && props.setState) {
      props.setState(tutorialState);
    }
  }, [props.open]);
  const riskBandLetters = getBaseThemeName() === "hkma" ? "ABCDE" : "ABC";
  const steps: TourStepProps[] = [{
    title: "",
    description: <>
          Click on &lsquo;<b>Single Asset Screen</b>&rsquo; from the main menu
          {getBaseThemeName() === "hkma" && <Image width={480} alt="Indication to click Single Asset Screen from the main menu" src="/img/tours/single/single_nav.jpg" />}
        </>
  }, {
    title: "To enter a location using an address:",
    description: <>
          <p>
            Under &lsquo;<b>Search For A Location</b>&rsquo;, enter your chosen address in the type field. Select the
            appropriate address from drop-down prompts if available. If the address result is not accurate, click a
            point on the map to drop the location pin to a more precise location.
          </p>
          <Alert description={<>
                NOTE: Please enter addresses in the format specified by local post authorities. Do not enter unit
                numbers, suite numbers or floor numbers. Avoid business names or landmarks. For more information, read
                the <GeocodingHelpButton isLink>geocoding guide</GeocodingHelpButton> here.
              </>} showIcon type="error" />
        </>,
    target: () => refs.address.current
  }, {
    title: "To enter a location using the map:",
    description: <>
          Under{" "}
          <b>
            &lsquo;<b>Select A Point</b>&rsquo;
          </b>
          , click on an asset location in the map window to drop a location pin. This will generate a Latitude and
          Longitude value in the{" "}
          <b>
            &lsquo;<b>Search For A Location</b>&rsquo;
          </b>{" "}
          field. Click and drag anywhere in the map window to navigate. Click &lsquo;<b>+</b>&rsquo; button to zoom in,
          &lsquo;<b>-</b>&rsquo; to zoom out.
        </>,
    target: () => refs.map.current
  }, {
    title: "",
    description: <p>
          Under &lsquo;<b>Settings</b>&rsquo;, choose an asset type from the &lsquo;<b>Asset Type (Archetype)</b>&rsquo;
          selection drop-down menu.
          {getKbConfig().glossary.enabled && <>
              For more information on selecting archetypes, see &lsquo;
              <Link href={"/archetypes"}>Glossary &ndash; Archetype</Link>
              &rsquo;
            </>}
        </p>,
    target: () => refs.advancedSettings!.archetype.current
  }, {
    title: "",
    description: <p>
          Under &lsquo;<b>Settings</b>&rsquo;, enter an asset &lsquo;<b>Build Year</b>&rsquo;. If unsure, leave the
          &lsquo;<b>Default for Asset Type</b>&rsquo; value in the field, or select &lsquo;<b>Custom Value</b>&rsquo; to
          enter the exact/estimated &lsquo;<b>Build Year</b>&rsquo; if known.
        </p>,
    target: () => refs.advancedSettings!.buildYear.current
  }, {
    title: "",
    description: <>
          <p>
            Under &lsquo;<b>Settings</b>&rsquo;, enter the asset&apos;s &lsquo;<b>Floor Height Above Ground</b>&rsquo;.
            You can estimate this by selecting a foundation type from the drop-down menu, or choose &lsquo;
            <b>Custom Value</b>&rsquo; to enter a precise height value (metres).
          </p>
        </>,
    target: () => refs.advancedSettings!.heightAboveGround.current
  }, {
    title: "",
    description: <>
          <p>
            Under &lsquo;<b>{settings("advancedAnalysisSettings")}</b>&rsquo; choose whether to enable or disable{" "}
            {settings("anomalyManagementRp.title")} and {settings("defences.title")}. For more information about{" "}
            {settings("anomalyManagementRp.title")} or {settings("defences.title")},{" "}
            <Link href={"/glossary/anomaly-management-1"}>visit the Glossary</Link>.
          </p>
        </>,
    target: () => refs.advancedSettings!.advancedCollapse.current
  }, {
    title: "",
    description: <>
          Click &lsquo;<b>Review</b>&rsquo; to open the Review Upload modal.
        </>,
    target: () => refs.reviewModalTrigger.current
  }, {
    title: "",
    description: <>
          When ready, click &lsquo;<b>Check</b>
          &rsquo; to begin the analysis.
        </>,
    target: () => {
      return refs.reviewModal?.proceed?.current || null;
    }
  }, {
    title: "",
    description: <>
          At the top of the results modal, the asset & analysis input settings will be displayed for the point analysed.
        </>,
    target: () => refs.modal?.analysisInput.current || null
  }];
  if (getRiskBandsMode() !== "abc") {
    steps.push(...[{
      title: "",
      description: <>
              Select &lsquo;{t("simpleRiskRatings")}&rsquo; from the tabs to view {riskBandLetters} Ratings for this
              asset.
            </>,
      target: () => refs.modal?.analysisTabs.current || null
    }, {
      title: "",
      description: <>
              Use the drop-down menu under &lsquo;<b>RCP</b>&rsquo; to compare results between four different RCP
              scenarios &ndash; 2.6, 4.5, 6.0 or 8.5.
            </>,
      target: () => refs.modal?.rcp.current || null
    }, {
      title: "",
      description: <>
              Select &lsquo;Significant Hazards&rsquo; from the tabs to view a list of up to two individual hazards with
              the greatest Maximum-to-date Value-At-Risk (MVAR) value for this asset.
            </>,
      target: () => refs.modal?.analysisTabs.current || null
    }, {
      title: "",
      description: <>
              Select &lsquo;Flood Exposure&rsquo; from the tabs to view each asset&apos;s exposure to Riverine or
              Surface Water flooding under a 1in1500 return period.
            </>,
      target: () => refs.modal?.analysisTabs.current || null
    }]);
  }
  steps.push(...[{
    title: "",
    description: <>
            Click &lsquo;
            <b>Download .csv</b>
            &rsquo; to download data as a .csv file.
          </>,
    target: () => refs.modal?.downloadCsv.current || null
  }, {
    title: "",
    description: <>
            To go back to edit the asset Settings, click &lsquo;
            <b>Back</b>
            &rsquo;.
          </>,
    target: () => refs.modal?.back.current || null
  }, {
    title: "",
    description: <>
            To start a new Single Asset Screen, click &lsquo;
            <b>New Single Asset</b>
            &rsquo;.
          </>,
    target: () => refs.modal?.newSingleAsset.current || null
  }]);
  return <InteractiveTutorial<SingleAssetScreenState> steps={steps} {...props} data-sentry-element="InteractiveTutorial" data-sentry-component="SingleAssetScreenTutorial" data-sentry-source-file="single.tsx" />;
};